import { Injectable, signal, computed, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

// Settings interfaces
export interface DesktopWallpapers {
  desktopOne: string;
  desktopTwo: string;
  desktopThree: string;
  desktopFour: string;
}

export interface DisplaySettings {
  theme: 'dark' | 'light' | 'system';
  wallpaper: string; // Legacy: default wallpaper for backwards compatibility
  desktopWallpapers: DesktopWallpapers; // Per-desktop wallpapers
  fontSize: string | 'small' | 'medium' | 'large';
  animationsEnabled: boolean;
  reducedMotion: boolean;
}

export interface DesktopLabels {
  desktopOne?: string;
  desktopTwo?: string;
  desktopThree?: string;
  desktopFour?: string;
}

export interface DesktopSettings {
  defaultDesktop: number;
  showDesktopPreviews: boolean;
  doubleClickToMaximize: boolean;
  snapWindowsToEdges: boolean;
  /** Custom display names for each desktop (e.g. "Work", "Personal") */
  desktopLabels?: DesktopLabels;
}

export interface PrivacySettings {
  autoLockEnabled: boolean;
  autoLockTimeout: number; // in seconds
  showNotificationsOnLockScreen: boolean;
  rememberOpenApps: boolean;
  clearDataOnLogout: boolean;
}

export interface NotificationSettings {
  enabled: boolean;
  sound: boolean;
  badgeCount: boolean;
  previewText: boolean;
  doNotDisturb: boolean;
  doNotDisturbStart: string;
  doNotDisturbEnd: string;
}

export interface AccessibilitySettings {
  highContrast: boolean;
  largeText: boolean;
  screenReader: boolean;
  keyboardNavigation: boolean;
  focusIndicators: boolean;
}

export interface ChatSettings {
  background: string; // Background ID (e.g., 'default', 'white', 'light-blue', etc.)
}

export interface AppSettings {
  display: DisplaySettings;
  desktop: DesktopSettings;
  privacy: PrivacySettings;
  notifications: NotificationSettings;
  accessibility: AccessibilitySettings;
  chat: ChatSettings;
  language: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
}

const SETTINGS_STORAGE_KEY = 'ews_app_settings';

const DEFAULT_SETTINGS: AppSettings = {
  display: {
    theme: 'dark',
    wallpaper: '/assets/wallpapers/default.jpg',
    desktopWallpapers: {
      desktopOne: '/assets/wallpapers/default.jpg',
      desktopTwo: '/assets/wallpapers/default.jpg',
      desktopThree: '/assets/wallpapers/default.jpg',
      desktopFour: '/assets/wallpapers/default.jpg',
    },
    fontSize: 'medium',
    animationsEnabled: true,
    reducedMotion: false,
  },
  desktop: {
    defaultDesktop: 1,
    showDesktopPreviews: true,
    doubleClickToMaximize: true,
    snapWindowsToEdges: true,
    desktopLabels: {},
  },
  privacy: {
    autoLockEnabled: true,
    autoLockTimeout: 300, // 5 minutes
    showNotificationsOnLockScreen: false,
    rememberOpenApps: true,
    clearDataOnLogout: false,
  },
  notifications: {
    enabled: true,
    sound: true,
    badgeCount: true,
    previewText: true,
    doNotDisturb: false,
    doNotDisturbStart: '22:00',
    doNotDisturbEnd: '07:00',
  },
  accessibility: {
    highContrast: false,
    largeText: false,
    screenReader: false,
    keyboardNavigation: true,
    focusIndicators: true,
  },
  chat: {
    background: 'default',
  },
  language: 'en',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
};

// Available wallpapers
export const AVAILABLE_WALLPAPERS = [
  { id: 'default', name: 'Default', url: '/assets/wallpapers/default.jpg', preview: '/assets/wallpapers/default-thumb.jpg' },
  { id: 'aurora', name: 'Aurora', url: '/assets/wallpapers/aurora.jpg', preview: '/assets/wallpapers/aurora-thumb.jpg' },
  { id: 'mountains', name: 'Mountains', url: '/assets/wallpapers/mountains.jpg', preview: '/assets/wallpapers/mountains-thumb.jpg' },
  { id: 'ocean', name: 'Ocean', url: '/assets/wallpapers/ocean.jpg', preview: '/assets/wallpapers/ocean-thumb.jpg' },
  { id: 'forest', name: 'Forest', url: '/assets/wallpapers/forest.jpg', preview: '/assets/wallpapers/forest-thumb.jpg' },
  { id: 'city', name: 'City Night', url: '/assets/wallpapers/city.jpg', preview: '/assets/wallpapers/city-thumb.jpg' },
  { id: 'abstract', name: 'Abstract', url: '/assets/wallpapers/abstract.jpg', preview: '/assets/wallpapers/abstract-thumb.jpg' },
  { id: 'gradient', name: 'Gradient', url: '/assets/wallpapers/gradient.jpg', preview: '/assets/wallpapers/gradient-thumb.jpg' },

  { id: 'city', name: 'Sky Mountains', url: '/assets/wallpapers/1081111.jpg', preview: '/assets/wallpapers/gradient.jpg' },
  { id: 'space', name: 'Snow', url: '/assets/wallpapers/2083164.jpg', preview: '/assets/wallpapers/1081111.jpg' },
  { id: 'stars', name: 'Stars', url: '/assets/wallpapers/2835436.jpg', preview: '/assets/wallpapers/2083164.jpg' },
  { id: 'galaxy', name: 'Artic Snow', url: '/assets/wallpapers/3426361.jpg', preview: '/assets/wallpapers/2835436.jpg' },
  { id: 'nebula', name: 'Dark Cloudy Sky', url: '/assets/wallpapers/3996534.jpg', preview: '/assets/wallpapers/3426361.jpg' },
  { id: 'sunset', name: 'Cloud Over the Nountaiine', url: '/assets/wallpapers/6249396.jpg', preview: '/assets/wallpapers/3996534.jpg' },
  { id: 'winter', name: 'Winter Cup', url: '/assets/wallpapers/6623944.jpg', preview: '/assets/wallpapers/6249396.jpg' },
];

// Available languages
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
];

// Auto-lock timeout options
export const AUTO_LOCK_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
  { value: -1, label: 'Never' },
];

// Declare Meteor as a global to avoid TypeScript errors
declare const Meteor: any;

@Injectable({
  providedIn: 'root'
})
export class SettingsService implements OnDestroy {
  // Settings signal
  private readonly _settings = signal<AppSettings>(this.loadSettings());
  
  // Public readonly accessor
  readonly settings = this._settings.asReadonly();
  
  // Computed signals for specific settings
  readonly displaySettings = computed(() => this._settings().display);
  readonly desktopSettings = computed(() => this._settings().desktop);
  readonly privacySettings = computed(() => this._settings().privacy);
  readonly notificationSettings = computed(() => this._settings().notifications);
  readonly accessibilitySettings = computed(() => this._settings().accessibility);
  readonly chatSettings = computed(() => this._settings().chat);
  readonly theme = computed(() => this._settings().display.theme);
  readonly language = computed(() => this._settings().language);

  // Change tracking
  private readonly _hasUnsavedChanges = signal(false);
  readonly hasUnsavedChanges = this._hasUnsavedChanges.asReadonly();

  // Current user ID for database operations
  private currentUserId: string | null = null;
  private settingsSubscriptionHandle: any = null;
  private settingsAutorunHandle: any = null;
  private destroy$ = new Subject<void>();

  constructor() {
    // Apply theme on initialization
    this.applyTheme(this._settings().display.theme);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribe();
  }

  /**
   * Unsubscribe from Meteor subscriptions
   */
  private unsubscribe(): void {
    if (this.settingsSubscriptionHandle) {
      this.settingsSubscriptionHandle.stop();
      this.settingsSubscriptionHandle = null;
    }
    if (this.settingsAutorunHandle) {
      this.settingsAutorunHandle.stop();
      this.settingsAutorunHandle = null;
    }
  }

  /**
   * Set the current user ID for database operations
   */
  setUserId(userId: string): void {
    this.currentUserId = userId;
    // Load all settings from database when userId is set
    this.loadSettingsFromDatabase();
    this.subscribeToSettings();
  }

  /**
   * Get current user ID
   */
  getUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Load settings from localStorage (fallback)
   */
  private loadSettings(): AppSettings {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to load settings',
        text: error instanceof Error ? error.message : String(error),
      });
    }
    return { ...DEFAULT_SETTINGS };
  }

  /**
   * Subscribe to app settings via Meteor pub/sub
   */
  private subscribeToSettings(): void {
    if (!this.currentUserId || typeof Meteor === 'undefined') {
      return;
    }

    // Unsubscribe from previous subscription if exists
    if (this.settingsSubscriptionHandle) {
      this.settingsSubscriptionHandle.stop();
    }
    if (this.settingsAutorunHandle) {
      this.settingsAutorunHandle.stop();
    }

    // Subscribe to app settings for this user
    this.settingsSubscriptionHandle = Meteor.subscribe('appSettings', this.currentUserId, {
      onReady: () => {
        this.loadSettingsFromDatabase();
      },
      onError: (error: any) => {
        Swal.fire({
          icon: 'error',
          title: 'Error subscribing to app settings',
          text: error?.message ?? String(error),
        });
      }
    });

    // Set up reactive autorun to watch for changes
    this.settingsAutorunHandle = Meteor.autorun(() => {
      if (this.settingsSubscriptionHandle?.ready()) {
        this.loadSettingsFromDatabase();
      }
    });
  }

  /**
   * Load settings from database
   */
  private loadSettingsFromDatabase(): void {
    if (!this.currentUserId || typeof Meteor === 'undefined') {
      return;
    }

    Meteor.call('getAppSettings', this.currentUserId, (error: any, result: any) => {
      if (error) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to load settings from database',
          text: error?.message ?? String(error),
        });
        return;
      }

      if (result?.code === 200 && result?.data) {
        // Merge with defaults to ensure all properties exist
        const mergedSettings = this.mergeWithDefaults(result.data);
        this._settings.set(mergedSettings);
        this._hasUnsavedChanges.set(false);
        
        // Apply theme and accessibility settings
        this.applyTheme(mergedSettings.display.theme);
        this.applyAccessibilitySettings(mergedSettings.accessibility);
        
        // Save to localStorage as cache/fallback
        try {
          localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(mergedSettings));
        } catch (e) {
          Swal.fire({
            icon: 'error',
            title: 'Failed to sync settings to localStorage',
            text: e instanceof Error ? e.message : String(e),
          });
        }
      } else if (result?.code === 404) {
        // No settings in database, use localStorage or defaults
        const localSettings = this.loadSettings();
        this._settings.set(localSettings);
        // Save current settings to database
        this.saveSettingsToDatabase();
      }
    });
  }

  /**
   * Merge stored settings with defaults
   */
  private mergeWithDefaults(stored: Partial<AppSettings>): AppSettings {
    return {
      display: { ...DEFAULT_SETTINGS.display, ...stored.display },
      desktop: { ...DEFAULT_SETTINGS.desktop, ...stored.desktop },
      privacy: { ...DEFAULT_SETTINGS.privacy, ...stored.privacy },
      notifications: { ...DEFAULT_SETTINGS.notifications, ...stored.notifications },
      accessibility: { ...DEFAULT_SETTINGS.accessibility, ...stored.accessibility },
      chat: { ...DEFAULT_SETTINGS.chat, ...stored.chat },
      language: stored.language ?? DEFAULT_SETTINGS.language,
      dateFormat: stored.dateFormat ?? DEFAULT_SETTINGS.dateFormat,
      timeFormat: stored.timeFormat ?? DEFAULT_SETTINGS.timeFormat,
    };
  }

  /**
   * Save current settings to database and localStorage
   */
  saveSettings(): void {
    try {
      // Save to localStorage as cache/fallback
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this._settings()));
      this._hasUnsavedChanges.set(false);
      
      // Save all settings to database
      this.saveSettingsToDatabase();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to save settings',
        text: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Save all app settings to database
   */
  private saveSettingsToDatabase(): void {
    if (!this.currentUserId || typeof Meteor === 'undefined') {
      return; // Can't save without user ID
    }

    const allSettings = this._settings();
    
    Meteor.call('saveAppSettings', this.currentUserId, allSettings, (error: any, result: any) => {
      if (error) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to save settings to database',
          text: error?.message ?? String(error),
        });
      } else if (result?.code !== 200 && result?.code !== 201) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to save settings',
          text: result?.message ?? 'Unknown error',
        });
      }
      // Success: no toast—avoids firing on every keystroke when editing desktop names
    });
  }

  /**
   * Save desktop wallpapers to database (legacy method, now handled by saveSettingsToDatabase)
   * Kept for backwards compatibility
   */
  private saveWallpapersToDatabase(): void {
    // Now handled by saveSettingsToDatabase which saves all settings
    this.saveSettingsToDatabase();
  }

  /**
   * Update display settings
   */
  updateDisplaySettings(updates: Partial<DisplaySettings>): void {
    this._settings.update(settings => ({
      ...settings,
      display: { ...settings.display, ...updates }
    }));
    this._hasUnsavedChanges.set(true);

    // Apply theme immediately if changed
    if (updates.theme) {
      this.applyTheme(updates.theme);
    }
  }

  /**
   * Update wallpaper for a specific desktop
   */
  updateDesktopWallpaper(desktopId: keyof DesktopWallpapers | string, wallpaperUrl: string): void {
    this._settings.update(settings => ({
      ...settings,
      display: {
        ...settings.display,
        desktopWallpapers: {
          ...settings.display.desktopWallpapers,
          [desktopId]: wallpaperUrl
        }
      }
    }));
    this._hasUnsavedChanges.set(true);
    
    // Save to database immediately
    this.saveSettingsToDatabase();
  }

  /**
   * Get wallpaper for a specific desktop
   */
  getDesktopWallpaper(desktopId: keyof DesktopWallpapers): string {
    const desktopWallpapers = this._settings().display.desktopWallpapers;
    // Return the specific desktop wallpaper, or fall back to the default wallpaper
    return desktopWallpapers?.[desktopId] || this._settings().display.wallpaper;
  }

  /**
   * Update desktop settings
   */
  updateDesktopSettings(updates: Partial<DesktopSettings>): void {
    this._settings.update(settings => ({
      ...settings,
      desktop: { ...settings.desktop, ...updates }
    }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Get display label for a desktop (custom name or default "Desktop 1", etc.)
   */
  getDesktopLabel(desktopRef: string): string {
    const labels = this._settings().desktop.desktopLabels;
    const custom = labels?.[desktopRef as keyof DesktopLabels]?.trim();
    if (custom) return custom;
    const defaultNames: Record<string, string> = {
      desktopOne: 'Desktop 1',
      desktopTwo: 'Desktop 2',
      desktopThree: 'Desktop 3',
      desktopFour: 'Desktop 4',
    };
    return defaultNames[desktopRef] ?? desktopRef;
  }

  /** Raw stored label for a desktop (for editing in popups); empty if not set */
  getDesktopLabelValue(desktopRef: string): string {
    const labels = this._settings().desktop.desktopLabels;
    return (labels as Record<string, string>)?.[desktopRef] ?? '';
  }

  /**
   * Update privacy settings
   */
  updatePrivacySettings(updates: Partial<PrivacySettings>): void {
    this._settings.update(settings => ({
      ...settings,
      privacy: { ...settings.privacy, ...updates }
    }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Update notification settings
   */
  updateNotificationSettings(updates: Partial<NotificationSettings>): void {
    this._settings.update(settings => ({
      ...settings,
      notifications: { ...settings.notifications, ...updates }
    }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Update accessibility settings
   */
  updateAccessibilitySettings(updates: Partial<AccessibilitySettings>): void {
    this._settings.update(settings => ({
      ...settings,
      accessibility: { ...settings.accessibility, ...updates }
    }));
    this._hasUnsavedChanges.set(true);

    // Apply accessibility changes immediately
    this.applyAccessibilitySettings(this._settings().accessibility);
    this.saveSettingsToDatabase();
  }

  /**
   * Update chat settings
   */
  updateChatSettings(updates: Partial<ChatSettings>): void {
    this._settings.update(settings => ({
      ...settings,
      chat: { ...settings.chat, ...updates }
    }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Update language
   */
  updateLanguage(language: string): void {
    this._settings.update(settings => ({ ...settings, language }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Update date format
   */
  updateDateFormat(dateFormat: string): void {
    this._settings.update(settings => ({ ...settings, dateFormat }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Update time format
   */
  updateTimeFormat(timeFormat: '12h' | '24h'): void {
    this._settings.update(settings => ({ ...settings, timeFormat }));
    this._hasUnsavedChanges.set(true);
    this.saveSettingsToDatabase();
  }

  /**
   * Reset all settings to defaults
   */
  resetToDefaults(): void {
    this._settings.set({ ...DEFAULT_SETTINGS });
    this._hasUnsavedChanges.set(true);
    this.applyTheme(DEFAULT_SETTINGS.display.theme);
    this.applyAccessibilitySettings(DEFAULT_SETTINGS.accessibility);
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: 'dark' | 'light' | 'system'): void {
    const html = document.documentElement;
    
    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      html.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      html.setAttribute('data-theme', theme);
    }

    // Update body class for backwards compatibility
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${theme === 'system' ? 
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme}`);
  }

  /**
   * Apply accessibility settings
   */
  private applyAccessibilitySettings(settings: AccessibilitySettings): void {
    const html = document.documentElement;
    
    html.classList.toggle('high-contrast', settings.highContrast);
    html.classList.toggle('large-text', settings.largeText);
    html.classList.toggle('reduced-motion', settings.highContrast);
    html.classList.toggle('keyboard-nav', settings.keyboardNavigation);
    html.classList.toggle('focus-visible', settings.focusIndicators);
  }

  /**
   * Export settings as JSON
   */
  exportSettings(): string {
    return JSON.stringify(this._settings(), null, 2);
  }

  /**
   * Import settings from JSON
   */
  importSettings(json: string): boolean {
    try {
      const imported = JSON.parse(json);
      const merged = this.mergeWithDefaults(imported);
      this._settings.set(merged);
      this._hasUnsavedChanges.set(true);
      this.applyTheme(merged.display.theme);
      this.applyAccessibilitySettings(merged.accessibility);
      return true;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to import settings',
        text: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /**
   * Get auto-lock timeout in milliseconds
   */
  getAutoLockTimeoutMs(): number {
    const timeout = this._settings().privacy.autoLockTimeout;
    return timeout === -1 ? -1 : timeout * 1000;
  }
}

